import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { apiService, getApiBaseUrl } from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";
import ConfirmDialog from "../common/ConfirmDialog";

interface AgentDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AgentDownloadModal({
  isOpen,
  onClose,
}: AgentDownloadModalProps) {
  const [enrollmentToken, setEnrollmentToken] = useState<string>("");
  const [agentVersion, setAgentVersion] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadEnrollmentToken();
      loadAgentVersion();
    }
  }, [isOpen]);

  const loadEnrollmentToken = async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await apiService.getEnrollmentToken();
      if (response.success && response.data) {
        setEnrollmentToken(response.data.enrollment_token);
      } else {
        setError("Erro ao carregar código de instalação");
      }
    } catch (err: any) {
      setError("Erro ao carregar código de instalação");
      console.error("Error loading enrollment token:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAgentVersion = async () => {
    try {
      const response = await apiService.getAgentVersion();
      if (response.success && response.version) {
        setAgentVersion(response.version);
      }
    } catch (err: any) {
      console.error("Error loading agent version:", err);
      // Não mostra erro para versão, é informação extra
    }
  };

  const handleCopyToken = async () => {
    if (enrollmentToken) {
      try {
        // Try modern clipboard API first (requires HTTPS or localhost)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(enrollmentToken);
        } else {
          // Fallback for older browsers or non-HTTPS
          const textArea = document.createElement("textarea");
          textArea.value = enrollmentToken;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand("copy");
          } catch (err) {
            console.error("Fallback copy failed:", err);
          }
          document.body.removeChild(textArea);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        // Try fallback even if modern API fails
        try {
          const textArea = document.createElement("textarea");
          textArea.value = enrollmentToken;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (fallbackErr) {
          console.error("Fallback copy also failed:", fallbackErr);
        }
      }
    }
  };

  const handleRegenerateToken = () => {
    setShowConfirmDialog(true);
  };

  const confirmRegenerateToken = async () => {
    setShowConfirmDialog(false);

    try {
      setIsRegenerating(true);
      setError("");
      const response = await apiService.regenerateEnrollmentToken();
      if (response.success && response.data) {
        setEnrollmentToken(response.data.enrollment_token);
      } else {
        setError("Erro ao regenerar código de instalação");
      }
    } catch (err: any) {
      setError("Erro ao regenerar código de instalação");
      console.error("Error regenerating enrollment token:", err);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDownload = async () => {
    try {
      console.log("[AGENT DOWNLOAD] Iniciando download...");
      setError("");
      const downloadUrl = apiService.getAgentDownloadUrl();

      console.log("[AGENT DOWNLOAD] URL:", {
        downloadUrl,
        origin: window.location.origin,
        fullUrl: downloadUrl,
        timestamp: new Date().toISOString(),
      });

      // Primeiro, fazer requisição OPTIONS (preflight) para verificar CORS
      console.log("[AGENT DOWNLOAD] Fazendo requisição OPTIONS (preflight)...");
      try {
        const optionsResponse = await fetch(downloadUrl, {
          method: "OPTIONS",
        });
        console.log("[AGENT DOWNLOAD] Resposta OPTIONS:", {
          status: optionsResponse.status,
          statusText: optionsResponse.statusText,
          headers: Object.fromEntries(optionsResponse.headers.entries()),
          corsHeaders: {
            "access-control-allow-origin": optionsResponse.headers.get(
              "access-control-allow-origin"
            ),
            "access-control-allow-methods": optionsResponse.headers.get(
              "access-control-allow-methods"
            ),
            "access-control-allow-headers": optionsResponse.headers.get(
              "access-control-allow-headers"
            ),
          },
        });
      } catch (optionsError: any) {
        console.error("[AGENT DOWNLOAD] Erro na requisição OPTIONS:", {
          error: optionsError.message,
          name: optionsError.name,
          stack: optionsError.stack,
        });
      }

      // Fetch the file as blob (sem credentials para evitar problema de CORS)
      console.log("[AGENT DOWNLOAD] Fazendo requisição GET...");
      const response = await fetch(downloadUrl, {
        method: "GET",
      });

      console.log("[AGENT DOWNLOAD] Resposta GET:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
        corsHeaders: {
          "access-control-allow-origin": response.headers.get(
            "access-control-allow-origin"
          ),
          "access-control-allow-methods": response.headers.get(
            "access-control-allow-methods"
          ),
          "access-control-allow-headers": response.headers.get(
            "access-control-allow-headers"
          ),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[AGENT DOWNLOAD] Resposta não OK:", {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || "Erro ao baixar arquivo" };
        }

        throw new Error(
          errorData.message || `Erro ${response.status}: ${response.statusText}`
        );
      }

      // Get the blob
      console.log("[AGENT DOWNLOAD] Convertendo resposta para blob...");
      const blob = await response.blob();
      console.log("[AGENT DOWNLOAD] Blob criado:", {
        size: blob.size,
        type: blob.type,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "AgentSetup.exe";
      document.body.appendChild(link);
      console.log("[AGENT DOWNLOAD] Iniciando download do arquivo...");
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log("[AGENT DOWNLOAD] Download concluído com sucesso!");
    } catch (err: any) {
      console.error("[AGENT DOWNLOAD] ERRO:", {
        message: err.message,
        name: err.name,
        stack: err.stack,
        isCORS:
          err.message?.includes("CORS") ||
          err.message?.includes("blocked") ||
          err.message?.includes("network"),
        isNetworkError:
          err.message === "Failed to fetch" || err.message?.includes("network"),
        fullError: err,
      });
      setError(err.message || "Erro ao baixar instalador do agente");
    }
  };

  const handleCopyDownloadLink = async () => {
    const downloadUrl = apiService.getAgentDownloadUrl();
    try {
      // Try modern clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(downloadUrl);
      } else {
        // Fallback for older browsers or non-HTTPS
        const textArea = document.createElement("textarea");
        textArea.value = downloadUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand("copy");
        } catch (err) {
          console.error("Fallback copy failed:", err);
        }
        document.body.removeChild(textArea);
      }
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      // Try fallback even if modern API fails
      try {
        const textArea = document.createElement("textarea");
        textArea.value = downloadUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } catch (fallbackErr) {
        console.error("Fallback copy also failed:", fallbackErr);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-50 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3
                  className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                  id="modal-title"
                >
                  Download do Agente
                </h3>
                {agentVersion && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                    v{agentVersion}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <p className="text-sm text-red-800 dark:text-red-400">
                  {error}
                </p>
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Código de Instalação
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        readOnly
                        value={enrollmentToken}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyToken}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {copied ? (
                        <>
                          <span className="text-green-600 dark:text-green-400">
                            Copiado!
                          </span>
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    URL do Backend (Copie para o Instalador)
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        readOnly
                        value={getApiBaseUrl()}
                        className="block w-full px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-md shadow-sm bg-blue-50 dark:bg-blue-900/10 text-blue-900 dark:text-blue-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const url = getApiBaseUrl();
                        if (
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          navigator.clipboard.writeText(url);
                        } else {
                          const textArea = document.createElement("textarea");
                          textArea.value = url;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand("copy");
                          document.body.removeChild(textArea);
                        }
                        // Visual feedback reuse existing state or add new one?
                        // Let's use simple alert-like or just reuse 'copied' state if user doesn't mind,
                        // but better to have separate state. I'll stick to inline logic for now to save state complexity
                        // actually, existing 'copied' state is tied to token button.
                        // I will add a small 'Copied!' effect text inside button temporarily.
                        const btn = document.getElementById("btn-copy-url");
                        if (btn) {
                          const originalText = btn.innerHTML;
                          btn.innerHTML =
                            '<span class="text-green-600">Copiado!</span>';
                          setTimeout(
                            () => (btn.innerHTML = originalText),
                            2000
                          );
                        }
                      }}
                      id="btn-copy-url"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                      Copiar
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-400">
                    Copie o <strong>Código de Instalação</strong> e a{" "}
                    <strong>URL do Backend</strong> acima e insira nos campos
                    correspondentes durante a instalação do agente.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                      Baixar Instalador
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyDownloadLink}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {copiedLink ? (
                        <>
                          <span className="text-green-600 dark:text-green-400">
                            Link copiado!
                          </span>
                        </>
                      ) : (
                        <>
                          <LinkIcon className="h-5 w-5 mr-2" />
                          Copiar Link
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleRegenerateToken}
                    disabled={isRegenerating}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRegenerating ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Gerando...</span>
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="h-5 w-5 mr-2" />
                        Gerar Novo Código
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Gerar Novo Código de Instalação"
        message={`⚠️ ATENÇÃO: Ao gerar um novo código de instalação:

• O código atual será INVALIDADO imediatamente
• Instalações em andamento com o código anterior NÃO funcionarão
• Apenas novas instalações poderão usar o novo código

Tem certeza que deseja continuar?`}
        confirmText="Sim, Gerar Novo Código"
        cancelText="Cancelar"
        type="warning"
        onConfirm={confirmRegenerateToken}
        onCancel={() => setShowConfirmDialog(false)}
        isLoading={isRegenerating}
      />
    </div>
  );
}
